/* eslint-disable arrow-body-style */
import { LANGUAGE_EN, LANGUAGE_CN } from '@home/_config/config';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { RELATION_JOINERS } from '@intelligence/components/GenreSelect/constants';

import type {
  IOption,
  IOptionWithChildren,
  IGenreSourcesGenreOptions,
  ITag,
  ITextable,
  IGenreSource,
  IGenreSources,
  IGenreValuable,
  IGenreSelectValue,
  IGenreCheckboxOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';

const localeFields: {
  [key: string]: keyof ITextable;
} = {
  [LANGUAGE_EN]: 'text',
  [LANGUAGE_CN]: 'textCN',
};

const localeBubbleFields: {
  [key: string]: keyof ITextable;
} = {
  [LANGUAGE_EN]: 'bubbleText',
  [LANGUAGE_CN]: 'bubbleTextCN',
};

export const getLocaleText = (locale: string, texts: ITextable): string => {
  const textLocaleField = localeFields[LANGUAGE_EN];
  const localeField = localeFields[locale] || textLocaleField;
  return texts?.[localeField] || texts?.[textLocaleField] || '';
};

export const getBubbleLabel = (locale: string, texts: ITextable): string => {
  const textLocaleField = localeBubbleFields[LANGUAGE_EN];
  const localeField = localeBubbleFields[locale] || textLocaleField;
  return texts?.[localeField] || texts?.[textLocaleField] || '';
};

const chartsSorter = <T extends string>(a: T, b: T): number => a?.localeCompare(b);

const genresSorter = <T extends IOption>(a: T, b: T): number => {
  const aName = a?.text || '';
  const bName = b?.text || '';
  return chartsSorter(aName, bName);
};

const sortGenresOptions = <T extends IOptionWithChildren>(genres?: T[]): T[] => {
  if (!Array.isArray(genres)) return [];
  const genresTmp = [...genres].map((genre) => {
    const subGenres = genre?.children || [];
    const subGenresSorted = [...subGenres].sort(genresSorter);
    return {
      ...(genre || {}),
      children: subGenresSorted,
    };
  });
  return genresTmp.sort(genresSorter);
};

export const sortGenreSourcesGenreOptions = (genres?: IGenreSourcesGenreOptions): IGenreSourcesGenreOptions => {
  if (!genres) return {};
  return Object.keys(genres).reduce((current, source) => {
    if (!source) return current;
    const options = sortGenresOptions(genres[source]);
    return {
      ...current,
      [source]: options,
    };
  }, {});
};

/**
 * 递归收集已勾选的品类路径（支持多层级）
 * @param node 当前品类节点
 * @param locale 语言环境
 * @param currentPath 当前路径（用于记录从根节点到当前节点的完整路径）
 * @param parentChecked 父节点是否已完全勾选（如果父节点已完全勾选，子节点不需要单独生成tag）
 * @returns 已勾选的品类tag数组
 */
const collectCheckedGenreTagsRecursive = (
  node: IGenreCheckboxOptionWithChildren,
  locale: string,
  currentPath: Array<{ label: string; value: string }>,
  parentChecked: boolean,
): ITag[] => {
  const tags: ITag[] = [];
  const nodeValue = node?.value;
  const nodeChildren = node?.children;

  // 数据异常检查
  if (!nodeValue) return tags;
  // 没有子节点，直接返回
  if (!Array.isArray(nodeChildren) || nodeChildren.length === 0) {
    // 如果当前节点已勾选，生成tag
    if (node?.checked && !parentChecked) {
      const nodeName = getLocaleText(locale, node);
      const path = [...(currentPath || []), { label: nodeName, value: nodeValue }];
      const label = path.map((item) => item?.label || item?.value).join(' > ');
      const tagPath = path?.map((item) => item?.value);
      tags.push({
        key: tagPath.join('>'),
        label,
        value: nodeValue,
        data: { path: tagPath },
      });
    }
    return tags;
  }

  // 当前节点名称
  const nodeName = getLocaleText(locale, node);
  const nodeChecked = !!node?.checked;
  const nodeIndeterminate = !!node?.indeterminate;

  // 更新路径
  const newPath = [...(currentPath || []), { label: nodeName, value: nodeValue }];

  // 如果当前节点完全勾选，生成一个tag
  if (nodeChecked && !nodeIndeterminate && !parentChecked) {
    const label = newPath?.map((item) => item?.label || item?.value).join(' > ');
    const tagPath = newPath?.map((item) => item?.value);
    tags.push({
      key: tagPath.join('>'),
      label,
      value: nodeValue,
      data: { path: tagPath },
    });
  }

  // 如果当前节点半选或有子节点需要处理，递归处理子节点
  if (nodeIndeterminate || (!nodeChecked && nodeChildren?.some((child) => child?.checked))) {
    for (const child of nodeChildren) {
      const childTags = collectCheckedGenreTagsRecursive(
        child as IGenreCheckboxOptionWithChildren,
        locale,
        newPath,
        nodeChecked && !nodeIndeterminate, // 如果父节点完全勾选，子节点不需要单独生成tag
      );
      tags.push(...childTags);
    }
  }

  return tags;
};

/**
 * 收集已勾选的品类tag（旧逻辑，仅支持两级品类）
 * 用于非 App Magic 品类数据源
 * @param genre 主品类节点
 * @param locale 语言环境
 * @returns 已勾选的品类tag数组
 */
const collectCheckedGenreTagsLegacy = (genre: IGenreCheckboxOptionWithChildren, locale: string): ITag[] => {
  const tags: ITag[] = [];
  const genreValue = genre?.value;
  const subGenres = genre?.children;

  // 数据异常
  if (!genreValue || !Array.isArray(subGenres)) {
    return tags;
  }

  const genreName = getLocaleText(locale, genre);
  const genreRelation = genre?.relation || Relation.Or;
  const genreRelationJoiner = RELATION_JOINERS[genreRelation] || RELATION_JOINERS[Relation.Or];
  const subs: Array<{ label: string; value: string }> = [];

  for (const subGenre of subGenres) {
    const subGenreValue = subGenre?.value;
    // 数据异常
    if (!subGenreValue) continue;
    // 没勾选
    if (!subGenre?.checked) continue;
    const subGenreName = getLocaleText(locale, subGenre);
    subs.push({ label: subGenreName, value: subGenreValue });
  }

  if (subs.length > 0) {
    const label = subs.map((item) => item?.label || item?.value)?.join(genreRelationJoiner);
    tags.push({
      key: genreValue,
      label: `${genreName}: ${label}`,
      value: genreValue,
    });
  }

  return tags;
};

export const convertCheckedGenres2Tags = (
  locale: string,
  genres: IGenreCheckboxOptionWithChildren[],
  source?: string,
): ITag[] => {
  if (!Array.isArray(genres)) return [];
  const tags: ITag[] = [];
  const isAppMagic = source === 'appmagic';

  for (const genre of genres) {
    const genreValue = genre?.value;
    // 数据异常
    if (!genreValue) continue;
    // 没勾选、没半选
    if (!(genre?.checked || genre?.indeterminate)) continue;

    if (isAppMagic) {
      // App Magic 品类：使用递归方法收集所有已勾选的品类tag（支持多层级）
      const genreTags = collectCheckedGenreTagsRecursive(genre, locale, [], false);
      tags.push(...genreTags);
    } else {
      // 非 App Magic 品类：使用旧逻辑（仅支持两级品类）
      const genreTags = collectCheckedGenreTagsLegacy(genre, locale);
      tags.push(...genreTags);
    }
  }

  return tags;
};

export const convertCheckedGenres2TagsStr = (data: ITag[]): string => {
  if (!Array.isArray(data)) return '';
  if (!data.length) return '';
  return data
    .filter((item) => item?.label)
    .map((item) => item.label)
    .join(', ');
};

/**
 * 递归转换选中的品类为 IGenreValuable 结构（支持多层级嵌套）
 * @param genres 品类数组
 * @returns 转换后的品类值数组（嵌套结构）
 */
export const convertCheckedGenres2GenresValuable = (genres: IGenreCheckboxOptionWithChildren[]): IGenreValuable[] => {
  if (!Array.isArray(genres) || genres.length === 0) {
    return [];
  }

  const result: IGenreValuable[] = [];

  genres.forEach((genre) => {
    const genreChecked = !!genre?.checked;
    const genreIndeterminate = !!genre?.indeterminate;

    // 跳过未选中且非半选状态的品类
    if (!(genreChecked || genreIndeterminate)) {
      return;
    }

    const genreValue = genre?.value;
    const subGenres = genre?.children || [];

    // 如果是完全勾选状态（checked=true），收集当前品类及其子级
    if (genreChecked) {
      const genreItem: IGenreValuable = {
        value: genreValue,
        relation: genre?.relation || Relation.Or,
        children: [],
      };

      // 递归收集子级
      if (subGenres.length > 0) {
        const childValues = convertCheckedGenres2GenresValuable(subGenres as IGenreCheckboxOptionWithChildren[]);
        if (childValues.length > 0) {
          // 子级不需要 relation 字段，只保留 value 和 children
          genreItem.children = childValues.map((child) => {
            return {
              value: child?.value,
              children: child?.children,
            };
          });
        }
      }

      result.push(genreItem);
    } else if (genreIndeterminate && subGenres.length > 0) {
      // 半选状态：收集当前品类及其被勾选的子级
      const childValues = convertCheckedGenres2GenresValuable(subGenres as IGenreCheckboxOptionWithChildren[]);
      if (childValues.length > 0) {
        const genreItem: IGenreValuable = {
          value: genreValue,
          relation: genre?.relation || Relation.Or,
          children: childValues.map((child) => {
            return {
              value: child?.value,
              children: child?.children,
            };
          }),
        };
        result.push(genreItem);
      }
    }
  });

  return result;
};

/**
 * 校验品类数据源类型，返回有效的品类数据源类型
 * @param sources 品类数据源类型列表
 * @param source 品类数据源类型
 * @returns 有效的品类数据源类型
 */
export const getValidSource = (sources: IGenreSources, source?: IGenreSource): IGenreSource => {
  return source && sources?.includes(source) ? source : sources?.[0] || '';
};

/**
 * 校验品类CheckboxOptions列表，返回有效的品类CheckboxOptions列表
 * @param genres 品类CheckboxOptions列表
 * @returns 有效的品类CheckboxOptions列表
 */
export const getValidGenres = (genres?: IGenreValuable[]): IGenreValuable[] => {
  return Array.isArray(genres) ? genres : [];
};

/**
 * 校验外部传入的组件value，返回有效的组件value
 * @param sources 外部传入的品类数据源类型
 * @param value 外部传入的组件value
 * @return 有效的组件value
 */
export const getValidValue = (sources: IGenreSources, value?: IGenreSelectValue): IGenreSelectValue => {
  const validSource = getValidSource(sources, value?.source);
  const validGenres = getValidGenres(value?.genres);
  return { source: validSource, genres: validGenres };
};
